// Geometry for `<OgGlobe>` — orthographic projection of the simplified world
// coastline + a graticule onto a 2D disc. Kept out of the component file so the
// render path stays pure JSX (the array-building happens here).

import { OG_GLOBE_COASTLINE } from './og-globe-coastline';

const D2R: number = Math.PI / 180;

interface P2 {
  x: number;
  y: number;
  front: boolean;
}

export interface GlobeGeometry {
  /** Front-facing continent polylines (svg `points` strings). */
  land: string[];
  /** Front-facing graticule polylines (meridians + parallels). */
  graticule: string[];
}

/** Orthographic projection of a [lng,lat] point onto the disc, in svg px. */
function project(lng: number, lat: number, yaw: number, tilt: number, r: number): P2 {
  const la = lat * D2R;
  const lo = lng * D2R + yaw;
  // Unit sphere → camera space (x right, y up, z toward viewer).
  const x0 = Math.cos(la) * Math.sin(lo);
  const y0 = Math.sin(la);
  const z0 = Math.cos(la) * Math.cos(lo);
  // Tilt about the x-axis.
  const ct = Math.cos(tilt);
  const st = Math.sin(tilt);
  const y1 = y0 * ct - z0 * st;
  const z1 = y0 * st + z0 * ct;
  return { x: r + x0 * r, y: r - y1 * r, front: z1 >= 0 };
}

/** Front-facing runs of a ring, each as an svg polyline `points` string. */
function ringToPaths(ring: number[][], yaw: number, tilt: number, r: number): string[] {
  const segments: string[] = [];
  let current: string[] = [];
  for (const pt of ring) {
    const lng = pt[0];
    const lat = pt[1];
    if (lng === undefined || lat === undefined) continue;
    const p = project(lng, lat, yaw, tilt, r);
    if (p.front) {
      current.push(`${p.x.toFixed(1)},${p.y.toFixed(1)}`);
    } else if (current.length > 1) {
      segments.push(current.join(' '));
      current = [];
    } else {
      current = [];
    }
  }
  if (current.length > 1) segments.push(current.join(' '));
  return segments;
}

/** A front-facing line of constant lng (meridian) or lat (parallel). */
function lineToPath(
  fixed: number,
  isMeridian: boolean,
  yaw: number,
  tilt: number,
  r: number,
): string | null {
  const pts: string[] = [];
  const from = isMeridian ? -90 : -180;
  const to = isMeridian ? 90 : 180;
  for (let v = from; v <= to; v += 5) {
    const p = isMeridian ? project(fixed, v, yaw, tilt, r) : project(v, fixed, yaw, tilt, r);
    if (p.front) pts.push(`${p.x.toFixed(1)},${p.y.toFixed(1)}`);
  }
  return pts.length > 1 ? pts.join(' ') : null;
}

/** Build the projected continents + graticule for a globe of diameter `size`. */
export function buildGlobeGeometry(
  size: number,
  rotationDeg: number,
  tiltDeg: number,
): GlobeGeometry {
  const r = size / 2;
  const yaw = rotationDeg * D2R;
  const tilt = tiltDeg * D2R;

  const land: string[] = [];
  for (const ring of OG_GLOBE_COASTLINE) {
    for (const seg of ringToPaths(ring, yaw, tilt, r)) land.push(seg);
  }

  const graticule: string[] = [];
  for (let lngLine = -180; lngLine < 180; lngLine += 30) {
    const path = lineToPath(lngLine, true, yaw, tilt, r);
    if (path) graticule.push(path);
  }
  for (let latLine = -60; latLine <= 60; latLine += 30) {
    const path = lineToPath(latLine, false, yaw, tilt, r);
    if (path) graticule.push(path);
  }

  return { land, graticule };
}
